import { EditorState } from "../features/editor/editorSlice";
import Connection, {
	ConnectionIo,
	ConnectionIoMemoryRef,
	ConnectionIoOperator,
} from "../features/editor/interfaces/components/Connection";
import Device, {
	Config,
} from "../features/editor/interfaces/components/Device";
import Memory from "../features/editor/interfaces/components/Memory";
import MemoryRef from "../features/editor/interfaces/components/MemoryRef";
import Metadata from "../features/editor/interfaces/components/Metadata";
import Operator, {
	Io,
} from "../features/editor/interfaces/components/Operator";
import { Types } from "../features/editor/interfaces/components/Variable";
import { Logics } from "../features/logics/logics/Logics";
import { LogicsConfig } from "../features/logics/logics/LogicsConfig";
import {
	LogicsIo,
	LogicsOperator,
} from "../features/logics/logics/LogicsOperator";
import { LogicsTypes } from "../features/logics/logics/LogicsVariable";
import { nonNullableFilter } from "./nonNullableFilter";
import { Point2D } from "./Point2D";

export interface CompilationErrors {
	id: string;
	type: "memoryRef" | "operator" | "connection";
	box: Point2D;
	message: string;
	sheet: number;
}
export interface ProjectGraph {
	version: ProjectVersion;
	devices: ProjectDevice[];
	sheet: ProjectSheet;
	warnings: CompilationErrors[];
}

export type ProjectVersion = [number, number, number];

export interface ProjectSheet {
	memories: ProjectMemory[];
	operators: ProjectOperator[];
}

export interface ProjectMemory {
	name: string;
	broadcast: boolean;
	address: string;
	id: number;
	realId: number;
	variables: ProjectMemoryVariable[];
	data: ProjectMemoryData[];
	group?: number | null;
}

export interface ProjectMemoryVariable {
	variable_name: string;
	value: any;
}

export interface ProjectMemoryData {
	size: string;
	value: string | string[];
}

export interface ProjectOperator {
	id: number;
	gridPosition: ProjectOperatorGridPosition;
	name: string;
	inputs: number[][];
	outputs: number[][];
	variables: ProjectOperatorVariable[];
}

export interface ProjectOperatorGridPosition {
	x: number;
	y: number;
}

export interface ProjectOperatorVariable {
	variable_name: string;
	value: any;
}

export interface ProjectDevice {
	address: string;
	configs: ProjectDeviceConfig[];
}

export interface ProjectDeviceConfig {
	type: string;
	variables: ProjectDeviceConfigVariable[];
}

export interface ProjectDeviceConfigVariable {
	value: number;
}

export function pgbBuild(editor: EditorState, logics: Logics): ProjectGraph {
	const builder = new PgbCreateEditorVariables(logics, editor);

	let status: PgbStatus = {
		status: "building",
		builder,
	};

	while (status.status === "building") {
		status = status.builder.step();
	}

	if (status.status === "done") {
		return status.projectGraph;
	}

	throw new Error(`Invalid status: ${status}`);
}

type IoMap = Map<string, number[][]>;

interface PgbBuilding {
	status: "building";
	builder: ProjectGraphBuilder;
}

interface PgbDone {
	status: "done";
	projectGraph: ProjectGraph;
}

type PgbStatus = PgbBuilding | PgbDone;

class ProjectGraphBuilder {
	step(): PgbStatus {
		throw new Error("Base class method called");
	}
}

class PgbCreateEditorVariables extends ProjectGraphBuilder {
	constructor(
		private logics: Logics,
		private editor: EditorState,
	) {
		super();
	}

	step(): PgbStatus {
		const { logics, editor } = this;

		const editorDevices = editor.devices.filter(nonNullableFilter);
		const editorMemories = Object.values(editor.memories).flat();
		const editorMemoryRefs = editor.sheets.flatMap((sheet) => sheet.memoryRefs);
		const editorOperators = editor.sheets.flatMap((sheet) => sheet.operators);

		return {
			status: "building",
			builder: new PgbCreateMemoryMap(
				logics,
				editor,
				editorDevices,
				editorMemories,
				editorMemoryRefs,
				editorOperators,
			),
		};
	}
}

class PgbCreateMemoryMap extends ProjectGraphBuilder {
	getNextId: () => number;
	getNextRealId: () => number;
	createVariable: () => ProjectMemory;

	memoryMap: Map<string, ProjectMemory>;

	warnings: CompilationErrors[];
	errors: Array<CompilationErrors>;

	constructor(
		private logics: Logics,
		private editor: EditorState,
		private editorDevices: Device[],
		private editorMemories: Memory[],
		private editorMemoryRefs: MemoryRef[],
		private editorOperators: Operator[],
	) {
		super();

		const { getNextId, getNextRealId } = this.createIdGenerator();

		this.getNextId = getNextId;
		this.getNextRealId = getNextRealId;
		this.createVariable = () =>
			this.createVariableMemory(logics, getNextId, getNextRealId);

		this.memoryMap = this.createInitialMemoryMap();

		this.warnings = new Array<CompilationErrors>();
		this.errors = new Array<CompilationErrors>();
	}

	step(): PgbStatus {
		const {
			logics,
			editor,
			editorDevices,
			editorMemories,
			editorMemoryRefs,
			editorOperators,
			createVariable,
			memoryMap,
			warnings,
		} = this;

		this.validateProject();

		if (this.errors.length > 0) throw this.errors;

		this.addEditorMemoriesToMemoryMap();

		return {
			status: "building",
			builder: new PgbCreateConnectionVariables(
				logics,
				editor,
				editorDevices,
				editorMemories,
				editorMemoryRefs,
				editorOperators,
				memoryMap,
				createVariable,
				warnings,
			),
		};
	}

	private validateProject() {
		const operatorsIos = new Map<
			string,
			{
				inputs: Map<number, Array<number>> | undefined;
				outputs: Map<number, Array<number>> | undefined;
			}
		>();

		this.editor.sheets.forEach((sheet, idx) => {
			sheet.memoryRefs.forEach((memoryRef) => {
				const found = sheet.connections.find(
					(connection) =>
						memoryRef.id ===
						(connection.from as ConnectionIoMemoryRef).memoryRefId,
				);

				if (found === undefined && memoryRef.name === "INPUT") {
					this.warnings.push({
						id: memoryRef.id,
						type: "memoryRef",
						box: {
							x: memoryRef.boundingBox.left + memoryRef.boundingBox.width,
							y: memoryRef.boundingBox.height + memoryRef.boundingBox.top,
						},
						message: `Input ${memoryRef.name} IO not connected`,
						sheet: idx,
					});
				}

				if (memoryRef.sourceMemoryId === null) {
					this.errors.push({
						id: memoryRef.id,
						type: "memoryRef",
						box: {
							x: memoryRef.boundingBox.left + memoryRef.boundingBox.width,
							y: memoryRef.boundingBox.height + memoryRef.boundingBox.top,
						},
						message: `${memoryRef.name} doesn't have memory assigned`,
						sheet: idx,
					});
				}
			});

			sheet.connections.forEach((connection) => {
				const { from, to } = connection;

				if (from.type === "operator" && to.type === "operator") {
					const itemFrom = operatorsIos.get(from.operatorId);
					const itemTo = operatorsIos.get(to.operatorId);

					const inputs: Map<number, Array<number>> = itemTo?.inputs ||
					new Map();
					const outputs: Map<number, Array<number>> | undefined =
						itemFrom?.outputs || new Map();

					const inputIoToChange = inputs.get(to.ioGroup) || [];
					const outputToChange = outputs?.get(from.ioGroup) || [];

					inputs.set(to.ioGroup, [...inputIoToChange, to.ioIndex]);
					outputs.set(from.ioGroup, [...outputToChange, from.ioIndex]);

					operatorsIos.set(from.operatorId, {
						outputs: outputs,
						inputs: itemFrom?.inputs,
					});

					operatorsIos.set(to.operatorId, {
						outputs: itemTo?.outputs,
						inputs: inputs,
					});
				} else if (from.type === "memoryRef" && to.type === "operator") {
					const item = operatorsIos.get(to.operatorId);
					const inputs: Map<number, Array<number>> = item?.inputs || new Map();
					const outputs: Map<number, Array<number>> | undefined = item?.outputs;

					const inputIoToChange = inputs.get(to.ioGroup) || [];

					inputs.set(to.ioGroup, [...inputIoToChange, to.ioIndex]);
					operatorsIos.set(to.operatorId, {
						outputs: outputs,
						inputs: inputs,
					});
				} else if (from.type === "operator" && to.type === "memoryRef") {
					const item = operatorsIos.get(from.operatorId);
					const inputs: Map<number, Array<number>> | undefined = item?.inputs;
					const outputs: Map<number, Array<number>> = item?.outputs ||
					new Map();

					const outputIoToChange = outputs.get(from.ioGroup) || [];
					outputs.set(from.ioGroup, [...outputIoToChange, from.ioIndex]);

					operatorsIos.set(from.operatorId, {
						outputs: outputs,
						inputs: inputs,
					});

					this.validateOperatorToMemoryRefConnection(
						from,
						to,
						sheet.connections,
						idx,
					);
				}
			});
		});

		this.validateOperatorsIos(operatorsIos);
		this.validateVariables();
	}

	private validateOperatorsIos(
		operatorsIos: Map<
			string,
			{
				inputs: Map<number, Array<number>> | undefined;
				outputs: Map<number, Array<number>> | undefined;
			}
		>,
	) {
		const logicsOperators = this.logics.operator;

		this.editorOperators.forEach((operator) => {
			this.validateInputsIo(operator, logicsOperators, operatorsIos);
			this.validateOutputsIo(operator, logicsOperators, operatorsIos);
		});
	}

	private validateInputsIo(
		operator: Operator,
		logicsOperators: Record<string, LogicsOperator>,
		operatorsIos: Map<
			string,
			{
				inputs: Map<number, Array<number>> | undefined;
				outputs: Map<number, Array<number>> | undefined;
			}
		>,
	) {
		const {
			id,
			inputs,
			name,
			label,
			metadata: { variables },
		} = operator;

		inputs
			.filter((input) => input.enabled)
			.forEach((input) => {
				const ioGroup = logicsOperators[name].inputs.findIndex(
					(log) => log.label == input.name,
				) as number;

				const logic = logicsOperators[name].inputs[ioGroup];

				if (operatorsIos.get(id) === undefined) {
					this.createNewError(
						`Operator ${label}, with none IO connected`,
						operator,
					);
					return;
				}

				const operatorIo = operatorsIos.get(id);

				if (operatorIo?.inputs === undefined) {
					this.createNewError(
						`Operator ${label}, all inputs not connected`,
						operator,
					);
					return;
				}

				const inputsIo = operatorIo.inputs.get(ioGroup);

				if (logic.connection.mode === "single") {
					if (inputsIo === undefined) {
						this.createNewError(
							`Operator ${label}, connection on IO ${input.name} not found and is required`,
							operator,
						);
					}
				} else if (logic.connection.mode === "spread") {
					const variableCounter = variables[logic.connection.counter].content
						.value as number;

					const validPins = variableCounter == 0 ? 1 : variableCounter;

					if (variableCounter === 0 && inputsIo == undefined) return;

					if (inputsIo == undefined || inputsIo!.length < validPins) {
						const valuesToCheck = [...Array(validPins).keys()];

						valuesToCheck.forEach((pin) => {
							const numberedPin =
								validPins > 1 ? `${input.name}_${pin + 1}` : input.name;

							if (!inputsIo?.includes(pin)) {
								this.createNewError(
									`Operator ${label}, connection on IO ${numberedPin} not found and is required`,
									operator,
								);
								return;
							}
						});

						return;
					}
				} else {
					if (inputsIo == undefined) {
						this.createNewError(
							`Operator ${label}, connection on IO ${input.name} not found and is required`,
							operator,
						);
						return;
					}
				}
			});
	}

	private validateOutputsIo(
		operator: Operator,
		logicsOperators: Record<string, LogicsOperator>,
		operatorsIos: Map<
			string,
			{
				inputs: Map<number, Array<number>> | undefined;
				outputs: Map<number, Array<number>> | undefined;
			}
		>,
	) {
		const {
			id,
			outputs,
			name,
			label,
			metadata: { variables },
		} = operator;

		outputs
			.filter((output) => output.enabled)
			.forEach((output) => {
				const ioGroup = logicsOperators[name].outputs.findIndex(
					(log) => log.label == output.name,
				) as number;

				const logic = logicsOperators[name].outputs[ioGroup];

				if (operatorsIos.get(id) === undefined) return;

				const operatorIo = operatorsIos.get(id);

				if (operatorIo?.outputs === undefined) {
					this.createNewError(
						`Operator ${label}, all outputs not connected`,
						operator,
					);
					return;
				}

				const outputsIo = operatorIo.outputs.get(ioGroup);

				if (logic.connection.mode === "single") {
					if (outputsIo === undefined) {
						this.createNewError(
							`Operator ${label}, connection on IO ${output.name} not found and is required`,
							operator,
						);
					}
				} else if (logic.connection.mode === "spread") {
					const variableCounter = variables[logic.connection.counter].content
						.value as number;

					const validPins = variableCounter == 0 ? 1 : variableCounter;

					if (variableCounter === 0 && outputsIo == undefined) return;

					if (outputsIo == undefined || outputsIo!.length < validPins) {
						const valuesToCheck = [...Array(validPins).keys()];

						valuesToCheck.forEach((pin) => {
							const numberedPin =
								validPins > 1 ? `${output.name}_${pin + 1}` : output.name;

							if (!outputsIo?.includes(pin)) {
								this.createNewError(
									`Operator ${label}, connection on IO ${numberedPin} not found and is required`,
									operator,
								);
								return;
							}
						});

						return;
					}
				} else {
					if (outputsIo == undefined) {
						this.createNewError(
							`Operator ${label}, connection on IO ${output.name} not found and is required`,
							operator,
						);
						return;
					}
				}
			});
	}

	private validateOperatorToMemoryRefConnection(
		from: ConnectionIoOperator,
		to: ConnectionIoMemoryRef,
		connections: Connection[],
		sheet: number,
	) {
		const operator = this.editorOperators.find(
			(operator) => operator.id === from.operatorId,
		);
		if (operator === undefined) {
			throw new Error(`Operator ${from.operatorId} not found`);
		}

		const logicsOperator = this.logics.operator[operator.name];

		if (logicsOperator.outputs[from.ioGroup].connection.mode === "single") {
			this.connectionIsUnique(
				connections,
				from.ioGroup,
				from.operatorId,
				operator,
				sheet,
			);
		}
	}

	private validateVariables() {
		const { memory } = this.logics;

		this.editorMemories.forEach((memo) => {
			const config = memory[memo.name];

			if (config === undefined) {
				// Return because config for that variable don't exist for version, don't need to validate
				return;
			}

			Object.entries(memo.metadata.variables).forEach(([key, variable]) => {
				const associatedSizeVariable = config.variables.find(
					(cfg) => cfg.name === key,
				);

				if (associatedSizeVariable === undefined) {
					// Return because config for that variable don't exist for version, don't need to validate
					return;
				}

				if (
					associatedSizeVariable.content.type === "number" &&
					variable.content.type === "number"
				) {
					const { max, min } = associatedSizeVariable.content;

					if (max && (variable.content as Types.Number).value > max) {
						const memoryRef = this.editorMemoryRefs.find(
							(memoRef) => memoRef.sourceMemoryId === memo.id,
						);

						this.errors.push({
							id: `${memoryRef?.id}`,
							box: {
								x: memoryRef!.boundingBox.left + memoryRef!.boundingBox.width,
								y: memoryRef!.boundingBox.height + memoryRef!.boundingBox.top,
							},
							message: `Variable ${key} length bigger than ${max} on INPUT ${memo.label} `,
							type: "memoryRef",
							sheet: +memoryRef!.sheetId.split("-")[1],
						});
					}

					if (min && (variable.content as Types.Number).value < min) {
						const memoryRef = this.editorMemoryRefs.find(
							(memoRef) => memoRef.sourceMemoryId === memo.id,
						);

						this.errors.push({
							id: `${memoryRef?.id}`,
							box: {
								x: memoryRef!.boundingBox.left + memoryRef!.boundingBox.width,
								y: memoryRef!.boundingBox.height + memoryRef!.boundingBox.top,
							},
							message: `Variable ${key} length less than ${min} on INPUT ${memo.label} `,
							type: "memoryRef",
							sheet: +memoryRef!.sheetId.split("-")[1],
						});
					}
				}
			});
		});
	}

	private createNewError(message: string, operator: Operator) {
		const { id, boundingBox, sheetId } = operator;

		const sheetIndex = this.editor.sheets.findIndex(
			(sheet) => sheet.id == sheetId,
		);

		this.errors.push({
			id: id,
			box: {
				x: boundingBox.left + boundingBox.width,
				y: boundingBox.height + boundingBox.top,
			},
			type: "operator",
			message: message,
			sheet: sheetIndex,
		});
	}

	private connectionIsUnique(
		connections: Connection[],
		ioGroup: number,
		operatorId: string,
		operator: Operator,
		sheet: number,
	) {
		const componentsConnected = connections.filter(
			(connection) =>
				(connection.from as ConnectionIoOperator).ioGroup === ioGroup &&
				(connection.from as ConnectionIoOperator).operatorId === operatorId,
		);

		if (componentsConnected.length > 1) {
			const { outputs, name, boundingBox } = operator;

			componentsConnected.forEach((connection) => {
				this.errors?.push({
					id: connection.id,
					type: "connection",
					message: `${outputs[ioGroup].name}(${name}) connection mode is single`,
					box: {
						x: boundingBox.left + boundingBox.width,
						y: boundingBox.height + boundingBox.top,
					},
					sheet,
				});
			});
		}
	}

	private createIdGenerator() {
		let nextId = 0;
		let nextGeneratedId = 0xffff;

		const getNextRealId = () => {
			const id = nextGeneratedId;
			nextGeneratedId -= 1;
			return id;
		};

		const getNextId = () => {
			const id = nextId;
			nextId += 1;
			return id;
		};

		return { getNextId, getNextRealId };
	}

	private createVariableMemory(
		logics: Logics,
		getNextId: () => number,
		getNextRealId: () => number,
	) {
		return {
			id: getNextId(),
			realId: getNextRealId(),
			name: "VARIABLE",
			broadcast: false,
			address: "Local",
			variables: logics.memory["VARIABLE"].variables.map((variable) => {
				return {
					variable_name: variable.name,
					value: +variable.content.default,
				};
			}),
			data: logics.memory["VARIABLE"].data
				? logics.memory["VARIABLE"].data.map((d) => {
						return {
							...d,
							value: "",
						};
					})
				: [],
		};
	}

	private createInitialMemoryMap() {
		const memoryMap = new Map<string, ProjectMemory>();

		memoryMap.set("___NULL_IN", this.createVariable());
		memoryMap.set("___NULL_OUT", this.createVariable());

		return memoryMap;
	}

	private addEditorMemoriesToMemoryMap(): Map<string, ProjectMemory> {
		const {
			logics,
			editorDevices,
			editorMemories,
			memoryMap,
			getNextId,
			getNextRealId,
		} = this;

		editorMemories.forEach((memory) => {
			const variables = Object.entries(memory.metadata.variables).map(
				([variableName, variable]) => {
					if (variable.content.type === "enum") {
						const logicsVariable = logics.memory[memory.name].variables.find(
							(variable) => variable.name === variableName,
						)!;
						const value = (logicsVariable.content as LogicsTypes.LogicsEnum)
							.options[variable.content.value as string];

						return {
							variable_name: variableName,
							value: +value,
						};
					}

					return {
						variable_name: variableName,
						value: +variable.content.value,
					};
				},
			);

			const configData = logics.memory[memory.name].data?.map(
				(config) => config.size,
			);

			const data = Object.entries(memory.metadata.data)
				.filter(([size]) => configData?.includes(size))
				.map(([size, value]) => {
					return {
						size,
						value,
					};
				});

			let realId: number;
			let address: string;

			switch (memory.source.type) {
				case "local": {
					address = "Local";
					realId = getNextRealId();

					break;
				}
				case "address": {
					const deviceId = memory.source.deviceId;
					const device = editorDevices
						.filter(nonNullableFilter)
						.find((device) => device.id === deviceId);

					if (device === undefined) {
						throw new Error(`Device ${deviceId} not found`);
					}

					if (device.address === undefined) {
						throw new Error(`Device ${device.name} has no address`);
					}

					address = device.address;
					realId = memory.source.address;

					break;
				}
				case "io": {
					const { io, deviceId } = memory.source;
					const device = editorDevices
						.filter(nonNullableFilter)
						.find((device) => device.id === deviceId);

					if (device === undefined) {
						throw new Error(`Device ${deviceId} not found`);
					}

					if (device.address === undefined) {
						throw new Error(`Device ${device.name} has no address`);
					}

					address = device.address;

					const logicsDevice = logics.device[device.name];

					switch (memory.name) {
						case "INPUT":
							realId = logicsDevice.input[io].id;
							break;
						case "OUTPUT":
							realId = logicsDevice.output[io].id;
							break;
						default:
							throw new Error(
								`Memory ${memory.label} has "io" source, but name is invalid: ${memory.name}`,
							);
					}

					break;
				}
			}

			const projectMemory = {
				name: memory.name,
				broadcast: memory.broadcast,
				address,
				id: getNextId(),
				realId,
				variables,
				data,
			};

			memoryMap.set(memory.label, projectMemory);
		});

		return memoryMap;
	}
}

class PgbCreateConnectionVariables extends ProjectGraphBuilder {
	projectInputMap: IoMap;
	projectOutputMap: IoMap;
	groups: Map<number, Set<string>> = new Map();
	errors: Array<CompilationErrors>;
	memoriesLabels: Set<string>;

	constructor(
		private logics: Logics,
		private editor: EditorState,
		private editorDevices: Device[],
		private editorMemories: Memory[],
		private editorMemoryRefs: MemoryRef[],
		private editorOperators: Operator[],
		private memoryMap: Map<string, ProjectMemory>,
		private createVariable: () => ProjectMemory,
		private warnings: CompilationErrors[],
	) {
		super();

		this.projectInputMap = this.createIoMap("input");
		this.projectOutputMap = this.createIoMap("output");
		this.errors = new Array<CompilationErrors>();
		this.groups = this.createGroupForValidateVariables();
		this.memoriesLabels = new Set(
			this.editor.memories["VARIABLE"].map((memo) => memo.label),
		);
	}

	step(): PgbStatus {
		const {
			logics,
			editorDevices,
			editorOperators,
			memoryMap,
			projectInputMap,
			projectOutputMap,
			warnings,
		} = this;

		this.addEditorConnectionsToIoMap();

		if (this.errors.length > 0) throw this.errors;

		return {
			status: "building",
			builder: new PgbCreateProjectGraph(
				logics,
				editorDevices,
				editorOperators,
				memoryMap,
				projectInputMap,
				projectOutputMap,
				warnings,
			),
		};
	}

	private createGroupForValidateVariables() {
		const groups: Map<number, Set<string>> = new Map();
		let currentGroup = 0;

		this.editor.sheets.forEach((sheet) => {
			const firstConnection = sheet.connections[0];

			let memoryTo = null;
			let memoryFrom = null;
			let operatorTo = null;
			let operatorFrom = null;

			if (!firstConnection) return;

			if (firstConnection.to.type === "memoryRef") {
				const id = firstConnection.to.memoryRefId;

				memoryTo = sheet.memoryRefs.find((m) => m.id == id)?.sourceMemoryId;
			} else {
				operatorTo = firstConnection.to.operatorId;
			}

			if (firstConnection.from.type === "memoryRef") {
				const id = firstConnection.from.memoryRefId;
				memoryFrom = sheet.memoryRefs.find((m) => m.id == id)?.sourceMemoryId;
			} else {
				operatorFrom = firstConnection.from.operatorId;
			}

			const foundGroup = groups.entries().find(([group, keys]) => {
				return (
					(memoryTo && keys.has(memoryTo)) ||
					(memoryFrom && keys.has(memoryFrom)) ||
					(operatorTo && keys.has(operatorTo)) ||
					(operatorFrom && keys.has(operatorFrom))
				);
			});

			const newSet = new Set<string>();

			if (memoryTo) newSet.add(memoryTo);
			if (memoryFrom) newSet.add(memoryFrom);
			if (operatorTo) newSet.add(operatorTo);
			if (operatorFrom) newSet.add(operatorFrom);

			if (foundGroup) {
				groups.set(foundGroup[0], newSet);
			} else {
				currentGroup += 1;
				groups.set(currentGroup, newSet);
			}

			sheet.connections.forEach((connection) => {
				const { to, from } = connection;

				let toMemoFind = null;
				let fromMemoFind = null;
				let toOperatorFind = null;
				let fromOperatorFind = null;

				if (to.type === "memoryRef") {
					const id = to.memoryRefId;

					toMemoFind = sheet.memoryRefs.find((m) => m.id == id)?.sourceMemoryId;
				} else {
					toOperatorFind = to.operatorId;
				}

				if (from.type === "memoryRef") {
					const id = from.memoryRefId;
					fromMemoFind = sheet.memoryRefs.find(
						(m) => m.id == id,
					)?.sourceMemoryId;
				} else {
					fromOperatorFind = from.operatorId;
				}

				const foundGroup = groups.entries().find(([group, keys]) => {
					return (
						(toMemoFind && keys.has(toMemoFind)) ||
						(fromMemoFind && keys.has(fromMemoFind)) ||
						(toOperatorFind && keys.has(toOperatorFind)) ||
						(fromOperatorFind && keys.has(fromOperatorFind))
					);
				});

				const newSet = new Set<string>();

				if (toMemoFind) newSet.add(toMemoFind);
				if (fromMemoFind) newSet.add(fromMemoFind);
				if (toOperatorFind) newSet.add(toOperatorFind);
				if (fromOperatorFind) newSet.add(fromOperatorFind);

				if (foundGroup) {
					groups.set(foundGroup[0], new Set([...foundGroup[1], ...newSet]));

					return {
						...connection,
						group: foundGroup[0],
					};
				} else {
					currentGroup += 1;
					groups.set(currentGroup, newSet);

					return {
						...connection,
						group: currentGroup,
					};
				}
			});
		});

		return groups;
	}

	private createIoMap(ioType: "input" | "output") {
		return new Map(
			this.editorOperators.map((operator) => {
				const logicsOperator = this.logics.operator[operator.name];
				const logicsIos =
					ioType === "input" ? logicsOperator.inputs : logicsOperator.outputs;
				const operatorIos =
					ioType === "input" ? operator.inputs : operator.outputs;
				const counters = this.ioCounters(
					logicsIos,
					operatorIos,
					operator.metadata,
				);

				const ios = counters.map(
					(ioCounter) => new Array<number>(ioCounter.ioCount),
				);

				logicsIos.forEach((logicsIo, ioGroupIndex) => {
					const rawIos = ios[ioGroupIndex];
					const io = operatorIos[ioGroupIndex];

					if (!io.enabled) {
						if (ioType === "input") {
							rawIos.fill(0);
						} else {
							rawIos.fill(1);
						}
					}
				});

				return [operator.id, ios];
			}),
		);
	}

	private ioCounters(logicIos: LogicsIo[], ios: Io[], metadata: Metadata) {
		return logicIos.map((logicIo, index) =>
			this.ioCounter(logicIo, ios[index].enabled, metadata),
		);
	}

	private ioCounter(io: LogicsIo, enabled: boolean, metadata: Metadata) {
		switch (io.connection.mode) {
			case "single":
				return { ioCount: 1 };

			case "multiple":
				return { ioCount: 0 };

			case "spread":
				const counter = io.connection.counter;
				const ioCount = metadata.variables[counter].content.value as number;

				return { ioCount };
		}
	}

	private addEditorConnectionsToIoMap() {
		this.editor.sheets.forEach((sheet) => {
			sheet.connections.forEach((connection) => {
				const { from, to } = connection;

				if (from.type === "operator" && to.type === "operator") {
					this.setOperatorToOperatorConnection(from, to);
				} else if (from.type === "memoryRef" && to.type === "operator") {
					this.setMemoryRefToOperatorConnection(from, to);
				} else if (from.type === "operator" && to.type === "memoryRef") {
					this.setOperatorToMemoryRefConnection(from, to);
				}
			});
		});
	}

	private setOperatorToOperatorConnection(
		from: ConnectionIoOperator,
		to: ConnectionIoOperator,
	) {
		const key = this.ioToKey(from);

		if (!this.memoryMap.has(key)) {
			const projectMemory = this.createVariable();

			this.memoryMap.set(key, projectMemory);
		}

		const projectMemory = this.memoryMap.get(key);
		if (projectMemory === undefined) {
			const found = this.editorOperators.find(
				(operator) => operator.id === from.operatorId,
			);

			if (found) {
				const keys = key.split("/");
				const logic = this.logics.operator[found.label];
				const output = logic.outputs[Number(keys[3])];

				this.createNewError(
					`Error on connection of operator ${found.label} output ${output.label}, reconnect should fix it`,
					found,
				);
			}

			return;
		}

		const projectInput = this.projectInputMap.get(to.operatorId);
		if (projectInput === undefined) {
			throw new Error(`Operator ${to.operatorId} not found`);
		}

		projectInput[to.ioGroup][to.ioIndex] = projectMemory.id;

		const projectOutput = this.projectOutputMap.get(from.operatorId);
		if (projectOutput === undefined) {
			throw new Error(`Operator ${from.operatorId} not found`);
		}

		const operator = this.editorOperators.find(
			(operator) => operator.id === from.operatorId,
		);
		if (operator === undefined) {
			throw new Error(`Operator ${from.operatorId} not found`);
		}

		const logicsOperator = this.logics.operator[operator.name];

		if (logicsOperator.outputs[from.ioGroup].connection.mode === "multiple") {
			projectOutput[from.ioGroup].push(projectMemory.id);
		} else {
			projectOutput[from.ioGroup][from.ioIndex] = projectMemory.id;
		}
	}

	private setMemoryRefToOperatorConnection(
		from: ConnectionIoMemoryRef,
		to: ConnectionIoOperator,
	) {
		const memory = this.ioMemoryRefToMemory(from);

		if (!memory) return;
		const key = this.memoryToKey(memory);

		const projectMemory = this.memoryMap.get(key);
		if (projectMemory === undefined) {
			throw new Error(`Variable ${key} not found`);
		}

		const projectInput = this.projectInputMap.get(to.operatorId);
		if (projectInput === undefined) {
			throw new Error(`Operator ${to.operatorId} not found`);
		}

		projectInput[to.ioGroup][to.ioIndex] = projectMemory.id;
	}

	private setOperatorToMemoryRefConnection(
		from: ConnectionIoOperator,
		to: ConnectionIoMemoryRef,
	) {
		const memory = this.ioMemoryRefToMemory(to);
		if (!memory) return;

		const key = this.memoryToKey(memory);

		const projectMemory = this.memoryMap.get(key);
		if (projectMemory === undefined) {
			throw new Error(`Variable ${key} not found`);
		}

		const projectOutput = this.projectOutputMap.get(from.operatorId);
		if (projectOutput === undefined) {
			throw new Error(`Operator ${from.operatorId} not found`);
		}

		const operator = this.editorOperators.find(
			(operator) => operator.id === from.operatorId,
		);
		if (operator === undefined) {
			throw new Error(`Operator ${from.operatorId} not found`);
		}

		const logicsOperator = this.logics.operator[operator.name];

		if (logicsOperator.outputs[from.ioGroup].connection.mode === "multiple") {
			projectOutput[from.ioGroup].push(projectMemory.id);
		} else {
			projectOutput[from.ioGroup][from.ioIndex] = projectMemory.id;
		}
	}

	private createNewError(message: string, operator: Operator) {
		const { id, boundingBox, sheetId } = operator;

		const sheetIndex = this.editor.sheets.findIndex(
			(sheet) => sheet.id == sheetId,
		);

		this.errors.push({
			id: id,
			box: {
				x: boundingBox.left + boundingBox.width,
				y: boundingBox.height + boundingBox.top,
			},
			type: "operator",
			message: message,
			sheet: sheetIndex,
		});
	}

	private ioToKey(io: ConnectionIo): string {
		return Object.values(io).join("/");
	}

	private memoryToKey(memory: Memory): string {
		return memory.label;
	}

	private ioMemoryRefToMemory(io: ConnectionIoMemoryRef): Memory | undefined {
		const originMemoryRef = this.editorMemoryRefs.find(
			(memoryRef) => memoryRef.id === io.memoryRefId,
		);

		if (originMemoryRef === undefined) {
			throw new Error(`MemoryRef ${io.memoryRefId} not found`);
		}

		const originMemory = this.editorMemories.find(
			(memory) => memory.id === originMemoryRef.sourceMemoryId,
		);

		if (originMemory === undefined) {
			const sheetIndex = this.editor.sheets.findIndex(
				(sheet) => sheet.id == originMemoryRef.sheetId,
			);

			this.errors.push({
				id: originMemoryRef.id,
				type: "memoryRef",
				box: {
					x:
						originMemoryRef.boundingBox.left +
						originMemoryRef.boundingBox.width,
					y:
						originMemoryRef.boundingBox.height +
						originMemoryRef.boundingBox.top,
				},
				message: `${originMemoryRef.name} doesn't have memory assigned`,
				sheet: sheetIndex,
			});
			return;
			throw new Error(`Memory ${io.memoryRefId} ${sheetIndex} not found`);
		}

		return originMemory;
	}
}

class PgbCreateProjectGraph extends ProjectGraphBuilder {
	constructor(
		private logics: Logics,
		private editorDevices: Device[],
		private editorOperators: Operator[],
		private memoryMap: Map<string, ProjectMemory>,
		private projectInputMap: IoMap,
		private projectOutputMap: IoMap,
		private warnings: CompilationErrors[],
	) {
		super();
	}

	step(): PgbStatus {
		const { warnings } = this;

		const devices = this.toProjectDevices();
		const memories = Array.from(this.memoryMap.values());
		const operators = this.toProjectOperators();

		const projectGraph: ProjectGraph = {
			version: [1, 0, 0],
			devices,
			sheet: {
				memories,
				operators,
			},
			warnings,
		};

		return {
			status: "done",
			projectGraph,
		};
	}

	private toProjectDevices(): ProjectDevice[] {
		return this.editorDevices.filter(nonNullableFilter).map((device) => {
			const logicsConfigInput = this.logics.config["config_input"];

			const configInputs = device.inputs.map((input) => {
				const { id, name, ...variables } = input;

				const configVariables = this.getConfigVariables(
					logicsConfigInput,
					variables,
				);

				return {
					type: "CONFIG_INPUT",
					variables: [{ value: input.id }, ...configVariables],
				};
			});

			const logicsConfigOutput = this.logics.config["config_output"];
			const configOutputs = device.outputs.map((output) => {
				const { id, name, ...variables } = output;

				const configVariables = this.getConfigVariables(
					logicsConfigOutput,
					variables,
				);

				return {
					type: "CONFIG_OUTPUT",
					variables: [{ value: id }, ...configVariables],
				};
			});

			const deviceConfigs = Object.entries(device.configs)
				.flatMap(([type, config]) =>
					config.map((config) => [type, config] as [string, Config]),
				)
				.map(([type, config]) => {
					const logicsConfig = this.logics.config[type];

					return {
						type: type.toUpperCase(),
						variables: Object.entries(config.variables).map(
							([variableName, variable]) => {
								const logicsVariable = logicsConfig.variables.find(
									(variable) => variable.label === variableName,
								)!;
								const value =
									logicsVariable.content.type !== "enum"
										? variable.content.value
										: (logicsVariable.content as LogicsTypes.LogicsEnum)
												.options[variable.content.value as string];
								return { value: +value };
							},
						),
					};
				});

			const configs = configInputs.concat(configOutputs).concat(deviceConfigs);

			if (device.address === undefined) {
				throw new Error(`Device ${device.name} has no address`);
			}

			return {
				address: device.address,
				configs,
			};
		});
	}

	getConfigVariables(
		logics: LogicsConfig,
		variables: Record<string, string | number | boolean>,
	): ProjectDeviceConfigVariable[] {
		const configVariables: ProjectDeviceConfigVariable[] = [];

		logics.variables
			.filter((logic) => variables[logic.name] !== undefined)
			.forEach((logic) => {
				switch (logic.content.type) {
					case "enum":
						configVariables.push({
							value: (logic.content as LogicsTypes.LogicsEnum).options[
								`${variables[logic.name]}`
							],
						});
						break;
					default:
						configVariables.push({ value: +variables[logic.name] });
						break;
				}
			});

		return configVariables;
	}

	private toProjectOperators(): ProjectOperator[] {
		return this.editorOperators
			.map((operator, operatorIndex) => {
				const inputs = this.projectInputMap.get(operator.id);
				if (inputs === undefined) {
					throw new Error(`Operator ${operator.id} not found`);
				}

				const outputs = this.projectOutputMap.get(operator.id);

				if (outputs === undefined) {
					throw new Error(`Operator ${operator.id} not found`);
				}

				const logicsOperator = this.logics.operator[operator.name];
				const variables = Object.entries(operator.metadata.variables).map(
					([variableName, variable]) => {
						const ioGroup = logicsOperator.outputs.findIndex((output) => {
							if (output.connection.mode === "single") {
								return false;
							}

							return output.connection.counter === variableName;
						});

						if (ioGroup !== -1) {
							return {
								variable_name: variableName,
								value: outputs[ioGroup].length,
							};
						}

						if (variable.content.type === "enum") {
							const logicsVariable = this.logics.operator[
								operator.name
							].variables.find((variable) => variable.name === variableName)!;
							const value = (logicsVariable.content as LogicsTypes.LogicsEnum)
								.options[variable.content.value as string];

							return {
								variable_name: variableName,
								value: +value,
							};
						}

						return {
							variable_name: variableName,
							value: +variable.content.value,
						};
					},
				);

				const projectOperator: ProjectOperator = {
					id: operatorIndex,
					gridPosition: {
						x: operator.boundingBox.left,
						y: operator.boundingBox.top,
					},
					name: operator.name,
					inputs,
					outputs,
					variables,
				};

				return projectOperator;
			})
			.toSorted((a, b) => a.gridPosition.y - b.gridPosition.y);
	}
}
